import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../auth/firebaseAuth';
import { createUserWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { auth } from '../../auth/firebaseConfig';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { serverTimestamp } from 'firebase/firestore';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { insertDocument, updateSiteMetrics } from '../../utility/firebaseHelpers';
import { UserPlus } from 'lucide-react';
import { BaseUser } from '../../types/userTypes';
import googleLogoSvg from '../../../src/media/logo/svgs/google.svg';
import { useAnalyticsWithConsent } from '../../hooks/useAnalyticsWithConsent';
import { AnalyticsEvents } from '../../analytics/firebaseAnalytics';

interface SignUpData {
  email: string;
  password: string;
  confirmPassword: string;
  name: string;
  phone: string;
  role: 'landlord' | 'renter' | 'agent' | 'fsbo' | '';
  marketingConsent: boolean;
}

interface PasswordRequirement {
  id: string;
  label: string;
  regex: RegExp;
  validator?: (password: string) => boolean;
}

export const SignUp: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const returnUrl = searchParams.get('returnUrl');
  const fromCreateListing = searchParams.get('from') === 'create-listing';
  const fromLandlordToolkit = returnUrl?.includes('/landlord-toolkit') || false;
  const [step, setStep] = useState(1);
  const analytics = useAnalyticsWithConsent();
  const [googleSignupPending, setGoogleSignupPending] = useState(false);
  const [googleUserId, setGoogleUserId] = useState<string | null>(null);
  const [signUpData, setSignUpData] = useState<SignUpData>({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    phone: '',
    role: fromLandlordToolkit ? 'landlord' : '',
    marketingConsent: false,
  });
  const passwordRequirements: PasswordRequirement[] = [
    {
      id: 'length',
      label: 'Be at least 8 characters long',
      validator: (password: string) => password.length >= 8,
      regex: /.*/,
    },
    {
      id: 'uppercase',
      label: 'Contain at least one uppercase letter',
      regex: /[A-Z]/,
    },
    {
      id: 'lowercase',
      label: 'Contain at least one lowercase letter',
      regex: /[a-z]/,
    },
    {
      id: 'number',
      label: 'Contain at least one number',
      regex: /\d/,
    },
    {
      id: 'special',
      label: 'Contain at least one special character',
      regex: /[!@#$%^&*(),.?":{}|<>]/,
    },
    {
      id: 'spaces',
      label: 'Not contain spaces',
      validator: (password: string) => !password.includes(' '),
      regex: /.*/,
    },
  ];
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const seoTitle = 'Sign Up for RentSmart.ie | Create Your Free Account';
  const seoDescription =
    'Sign up for RentSmart.ie to browse rentals, list properties, manage tenants, and access property tools across Ireland.';

  useEffect(() => {
    // Track signup form start
    analytics.logEvent(AnalyticsEvents.SIGNUP_FORM_START, {
      page: 'signup',
      timestamp: new Date().toISOString(),
      from_create_listing: fromCreateListing,
    });

    const quickSignupData = localStorage.getItem('quickSignupData');
    if (quickSignupData) {
      const data = JSON.parse(quickSignupData);
      if (new Date().getTime() - new Date(data.timestamp).getTime() < 3600000) {
        // 1 hour
        setSignUpData((prev) => ({
          ...prev,
          name: data.m_name,
          email: data.email,
        }));
      }
      localStorage.removeItem('quickSignupData');
    }
  }, [analytics, fromCreateListing]);

  const checkPasswordRequirement = (
    requirement: PasswordRequirement,
    password: string
  ): boolean => {
    if (requirement.validator) {
      return requirement.validator(password);
    }
    return requirement.regex.test(password);
  };

  const validatePassword = (password: string): { isValid: boolean; error: string } => {
    const failedRequirements = passwordRequirements.filter(
      (requirement) => !checkPasswordRequirement(requirement, password)
    );

    if (failedRequirements.length > 0) {
      return {
        isValid: false,
        error: `Password must: ${failedRequirements[0].label.toLowerCase()}`,
      };
    }

    return { isValid: true, error: '' };
  };

  const validateStep1 = () => {
    if (!signUpData.email || !signUpData.password || !signUpData.confirmPassword) {
      setError('Please fill in all fields.');
      return false;
    }

    if (!/\S+@\S+\.\S+/.test(signUpData.email)) {
      setError('Please enter a valid email address.');
      return false;
    }

    const passwordValidation = validatePassword(signUpData.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.error);
      return false;
    }

    if (signUpData.password !== signUpData.confirmPassword) {
      setError('Passwords do not match.');
      return false;
    }

    return true;
  };

  const validateStep2 = () => {
    if (!signUpData.name || !signUpData.phone) {
      setError('Please enter your full name and phone number.');
      return false;
    }
    if (!signUpData.role) {
      setError('Please select whether you want to rent, list, manage, or sell properties.');
      return false;
    }
    return true;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSignUpData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
      setError('');
    }
  };

  const handlePrevStep = () => {
    setStep(1);
    setError('');
  };

  const createUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep2()) return;
    if (!agreeToTerms) {
      setError('You must agree to the Terms of Service to create an account.');
      return;
    }

    // Track signup button click
    analytics.logEvent(AnalyticsEvents.CLICK_SIGNUP_BUTTON, {
      signup_method: 'email',
      page: 'signup',
      user_role: signUpData.role,
      from_create_listing: fromCreateListing,
    });

    setIsLoading(true);
    setError('');

    try {
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        signUpData.email,
        signUpData.password
      );

      let userDefaultBio = 'I am looking for a place to rent.';
      if (signUpData.role === 'landlord') {
        userDefaultBio = 'I have a property available for rent or sale.';
      } else if (signUpData.role === 'agent') {
        userDefaultBio = 'I manage rental properties on behalf of landlords.';
      } else if (signUpData.role === 'fsbo') {
        userDefaultBio = 'I am selling my home directly (FSBO).';
      }

      const userData: BaseUser = {
        id: userCredential.user.uid,
        name: signUpData.name,
        email: signUpData.email,
        phone: signUpData.phone,
        accountVerification: {
          emailVerified: false,
          phoneVerified: false,
          identityVerified: false,
        },
        accountCreationDate: serverTimestamp(),
        bio: userDefaultBio,
        communicationPreferences: {
          emailOffers: signUpData.marketingConsent,
          pushNotifications: signUpData.marketingConsent,
        },
        listingPreferences: {
          showEmail: false,
          showMobile: false,
        },
        role: signUpData.role || 'renter',
        engagementEmailsEnabled: true, // Enable engagement email series for new users
        marketingConsent: signUpData.marketingConsent,
        ...(signUpData.role === 'fsbo' && {
          fsboProfiles: {
            progress: {
              hasSolicitor: false,
              hasBER: false,
              hasTitleDeeds: false,
              hasPlanningDocs: false,
              hasComplianceCerts: false,
              hasLPTClearance: false,
              hasMortgageInfo: false,
              hasManagementInfo: false,
              hasFixturesList: false,
              hasKeysReady: false,
            },
            progressHistory: {},
          },
        }),
      };

      await insertDocument('users', userData, userCredential.user.uid);

      // Update site metrics
      const metricsUpdate: Record<string, number> = {
        totalUsers: 1,
      };

      if (signUpData.role === 'renter') {
        metricsUpdate.totalRenters = 1;
      } else if (signUpData.role === 'landlord') {
        metricsUpdate.totalLandlords = 1;
      } else if (signUpData.role === 'agent') {
        metricsUpdate.totalPropertyAgents = 1;
      } else if (signUpData.role === 'fsbo') {
        metricsUpdate.totalFsboSellers = 1;
      }

      await updateSiteMetrics(metricsUpdate);

      // Track signup form completion
      analytics.logEvent(AnalyticsEvents.SIGNUP_FORM_COMPLETE, {
        signup_method: 'email',
        user_role: signUpData.role,
        user_id: userCredential.user.uid,
        from_create_listing: fromCreateListing,
        has_pending_listing: !!localStorage.getItem('pendingListing'),
      });

      // Check if user came from create listing flow
      const hasPendingListing = localStorage.getItem('pendingListing');
      if (returnUrl) {
        navigate(returnUrl);
      } else if (signUpData.role === 'fsbo') {
        navigate('/dashboard/fsbo');
      } else if (hasPendingListing) {
        navigate('/complete-listing');
      } else if (signUpData.role === 'landlord' || signUpData.role === 'agent') {
        navigate('/onboarding');
      } else {
        navigate('/browse');
      }
    } catch (error) {
      setError('Failed to create an account. Please try again.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    // Track signup button click
    analytics.logEvent(AnalyticsEvents.CLICK_SIGNUP_BUTTON, {
      signup_method: 'google',
      page: 'signup',
      from_create_listing: fromCreateListing,
    });

    setIsLoading(true);
    setError('');

    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);

      // If coming from landlord toolkit, auto-assign landlord role
      const isFromLandlordToolkit = returnUrl?.includes('/landlord-toolkit');

      if (isFromLandlordToolkit) {
        const defaultRole = 'landlord';
        const defaultBio = 'I have a property available for rent or sale.';

        const userData: BaseUser = {
          id: userCredential.user.uid,
          name: userCredential.user.displayName ?? 'User',
          email: userCredential.user.email ?? '',
          phone: userCredential.user.phoneNumber ?? '',
          accountVerification: {
            emailVerified: false,
            phoneVerified: false,
            identityVerified: false,
          },
          accountCreationDate: serverTimestamp(),
          bio: defaultBio,
          communicationPreferences: {
            emailOffers: false,
            pushNotifications: false,
          },
          listingPreferences: {
            showEmail: false,
            showMobile: false,
          },
          role: defaultRole,
          engagementEmailsEnabled: true,
          marketingConsent: false,
        };

        await insertDocument('users', userData, userCredential.user.uid);
        await updateSiteMetrics({ totalUsers: 1, totalLandlords: 1 });

        analytics.logEvent(AnalyticsEvents.SIGNUP_FORM_COMPLETE, {
          signup_method: 'google',
          user_role: defaultRole,
          user_id: userCredential.user.uid,
          from_create_listing: fromCreateListing,
        });

        navigate(returnUrl || '/landlord-toolkit');
      } else {
        // For regular signups, show role selection
        const userData: BaseUser = {
          id: userCredential.user.uid,
          name: userCredential.user.displayName ?? 'User',
          email: userCredential.user.email ?? '',
          phone: userCredential.user.phoneNumber ?? '',
          accountVerification: {
            emailVerified: false,
            phoneVerified: false,
            identityVerified: false,
          },
          accountCreationDate: serverTimestamp(),
          bio: 'RentSmart platform user',
          communicationPreferences: {
            emailOffers: false,
            pushNotifications: false,
          },
          listingPreferences: {
            showEmail: false,
            showMobile: false,
          },
          role: 'renter', // Temporary default
          engagementEmailsEnabled: true,
          marketingConsent: false,
        };

        // Set state to show role selection BEFORE creating user document
        // This ensures googleSignupPending is true before currentUser is detected
        setGoogleUserId(userCredential.user.uid);
        setSignUpData((prev) => ({
          ...prev,
          name: userCredential.user.displayName ?? 'User',
          email: userCredential.user.email ?? '',
          phone: userCredential.user.phoneNumber ?? '',
          role: '', // Reset role for selection
        }));
        setGoogleSignupPending(true);
        setIsLoading(false);

        await insertDocument('users', userData, userCredential.user.uid);
      }
    } catch (error) {
      setError('Failed to sign up with Google. Please try again.');
      console.error(error);
      setIsLoading(false);
    }
  };

  const completeGoogleSignup = async () => {
    if (!googleUserId || !signUpData.role) {
      setError('Please select how you would like to use RentSmart.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      let userDefaultBio = 'I am looking for a place to rent.';
      if (signUpData.role === 'landlord') {
        userDefaultBio = 'I have a property available for rent or sale.';
      } else if (signUpData.role === 'agent') {
        userDefaultBio = 'I manage rental properties on behalf of landlords.';
      } else if (signUpData.role === 'fsbo') {
        userDefaultBio = 'I am selling my home directly (FSBO).';
      }

      // Update user with selected role
      const { updateDocument } = await import('../../utility/firebaseHelpers');
      await updateDocument('users', googleUserId, {
        role: signUpData.role,
        bio: userDefaultBio,
        marketingConsent: signUpData.marketingConsent,
        communicationPreferences: {
          emailOffers: signUpData.marketingConsent,
          pushNotifications: signUpData.marketingConsent,
        },
        ...(signUpData.role === 'fsbo' && {
          fsboProfiles: {
            progress: {
              hasSolicitor: false,
              hasBER: false,
              hasTitleDeeds: false,
              hasPlanningDocs: false,
              hasComplianceCerts: false,
              hasLPTClearance: false,
              hasMortgageInfo: false,
              hasManagementInfo: false,
              hasFixturesList: false,
              hasKeysReady: false,
            },
            progressHistory: {},
          },
        }),
      });

      // Update site metrics
      const metricsUpdate: Record<string, number> = {
        totalUsers: 1,
      };
      if (signUpData.role === 'renter') {
        metricsUpdate.totalRenters = 1;
      } else if (signUpData.role === 'landlord') {
        metricsUpdate.totalLandlords = 1;
      } else if (signUpData.role === 'agent') {
        metricsUpdate.totalPropertyAgents = 1;
      } else if (signUpData.role === 'fsbo') {
        metricsUpdate.totalFsboSellers = 1;
      }
      await updateSiteMetrics(metricsUpdate);

      // Track signup form completion
      analytics.logEvent(AnalyticsEvents.SIGNUP_FORM_COMPLETE, {
        signup_method: 'google',
        user_role: signUpData.role,
        user_id: googleUserId,
        from_create_listing: fromCreateListing,
        has_pending_listing: !!localStorage.getItem('pendingListing'),
      });

      // Navigate based on role and context
      const hasPendingListing = localStorage.getItem('pendingListing');
      if (returnUrl) {
        navigate(returnUrl);
      } else if (signUpData.role === 'fsbo') {
        navigate('/dashboard/fsbo');
      } else if (hasPendingListing) {
        navigate('/complete-listing');
      } else if (signUpData.role === 'landlord' || signUpData.role === 'agent') {
        navigate('/onboarding');
      } else {
        navigate('/browse');
      }
    } catch (error) {
      setError('Failed to complete signup. Please try again.');
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (currentUser && !googleSignupPending) {
      if (returnUrl) {
        navigate(returnUrl);
      } else {
        navigate('/browse');
      }
    }
  }, [currentUser, navigate, returnUrl, googleSignupPending]);

  // Show role selection for Google signups
  if (googleSignupPending) {
    return (
      <>
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          <link rel="canonical" href="https://rentsmart.ie/signup" />
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://rentsmart.ie/signup" />
        </Helmet>
        <div className="w-full max-w-md mx-auto mt-8">
          <h1 className="sr-only">Sign Up for RentSmart.ie</h1>
        <div className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
          <div className="text-center mb-6">
            <UserPlus className="h-12 w-12 text-blue-500 mx-auto mb-2" />
            <h2 className="text-3xl font-bold mb-2 text-gray-800">Welcome to RentSmart!</h2>
            <p className="text-center text-gray-600">How would you like to use RentSmart?</p>
          </div>

          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}

          <fieldset className="mb-6">
            <legend className="block text-gray-700 text-sm font-semibold mb-2">
              What would you like to do?*
            </legend>
            <div className="flex flex-col space-y-2">
              <label
                htmlFor="google-role-renter"
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition ${signUpData.role === 'renter' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
              >
                <input
                  id="google-role-renter"
                  type="radio"
                  name="role"
                  value="renter"
                  checked={signUpData.role === 'renter'}
                  onChange={handleInputChange}
                  className="peer sr-only"
                />
                <div className="flex items-start">
                  <div
                    className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center ${signUpData.role === 'renter' ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                  >
                    {signUpData.role === 'renter' && (
                      <svg className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.418 0l-3.5-3.5a1 1 0 011.418-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      I want to rent a property
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Browse active listings, book viewings, and apply easily.
                    </p>
                  </div>
                </div>
              </label>

              <label
                htmlFor="google-role-landlord"
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition ${signUpData.role === 'landlord' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
              >
                <input
                  id="google-role-landlord"
                  type="radio"
                  name="role"
                  value="landlord"
                  checked={signUpData.role === 'landlord'}
                  onChange={handleInputChange}
                  className="peer sr-only"
                />
                <div className="flex items-start">
                  <div
                    className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center ${signUpData.role === 'landlord' ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                  >
                    {signUpData.role === 'landlord' && (
                      <svg className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.418 0l-3.5-3.5a1 1 0 011.418-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      I want to list a property
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Create listings, manage inquiries, and track performance.
                    </p>
                  </div>
                </div>
              </label>

              <label
                htmlFor="google-role-agent"
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition ${signUpData.role === 'agent' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
              >
                <input
                  id="google-role-agent"
                  type="radio"
                  name="role"
                  value="agent"
                  checked={signUpData.role === 'agent'}
                  onChange={handleInputChange}
                  className="peer sr-only"
                />
                <div className="flex items-start">
                  <div
                    className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center ${signUpData.role === 'agent' ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                  >
                    {signUpData.role === 'agent' && (
                      <svg className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.418 0l-3.5-3.5a1 1 0 011.418-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      I manage properties for clients
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Use the toolkit to manage multiple properties efficiently.
                    </p>
                  </div>
                </div>
              </label>

              <label
                htmlFor="google-role-fsbo"
                className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition ${signUpData.role === 'fsbo' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
              >
                <input
                  id="google-role-fsbo"
                  type="radio"
                  name="role"
                  value="fsbo"
                  checked={signUpData.role === 'fsbo'}
                  onChange={handleInputChange}
                  className="peer sr-only"
                />
                <div className="flex items-start">
                  <div
                    className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center ${signUpData.role === 'fsbo' ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                  >
                    {signUpData.role === 'fsbo' && (
                      <svg className="h-4 w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
                        <path
                          fillRule="evenodd"
                          d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.418 0l-3.5-3.5a1 1 0 011.418-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                  <div className="ml-3">
                    <span className="text-sm font-medium text-gray-900">
                      I want to sell my home
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      Sell directly (For Sale By Owner) with guided steps.
                    </p>
                  </div>
                </div>
              </label>
            </div>
          </fieldset>

          <div className="mb-6">
            <label className="inline-flex items-start">
              <input
                type="checkbox"
                checked={signUpData.marketingConsent}
                onChange={(e) =>
                  setSignUpData((prev) => ({
                    ...prev,
                    marketingConsent: e.target.checked,
                  }))
                }
                className="form-checkbox text-blue-500 mt-1"
              />
              <span className="ml-2 text-sm text-gray-700">
                Yes, I'd like to receive exclusive property updates, personalized recommendations,
                and special offers from RentSmart.ie. I can unsubscribe anytime.
              </span>
            </label>
          </div>

          <button
            onClick={completeGoogleSignup}
            disabled={isLoading || !signUpData.role}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {isLoading ? (
              <>
                <svg
                  className="animate-spin h-5 w-5 mr-2 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Completing Signup...
              </>
            ) : (
              'Continue'
            )}
          </button>
        </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href="https://rentsmart.ie/signup" />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://rentsmart.ie/signup" />
      </Helmet>
      <div className="w-full max-w-md mx-auto mt-8">
        <h1 className="sr-only">Sign Up for RentSmart.ie</h1>
      <div className="bg-white shadow-md rounded-lg px-8 pt-6 pb-8 mb-4">
        <div className="text-center mb-6">
          <UserPlus className="h-12 w-12 text-blue-500 mx-auto mb-2" />
          {fromLandlordToolkit ? (
            <>
              <h2 className="text-3xl font-bold mb-2 text-gray-800">Start Managing Your Rentals</h2>
              <p className="text-center text-gray-600 font-medium">
                Join 1,000+ landlords who manage smarter
              </p>
            </>
          ) : (
            <>
              <h2 className="text-3xl font-bold mb-2 text-gray-800">Create your account</h2>
              <p className="text-center text-gray-600">It only takes 2 minutes to get started</p>
            </>
          )}
        </div>

        {/* Landlord-specific value props */}
        {fromLandlordToolkit && (
          <div className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-5 rounded-lg">
            <div className="space-y-3">
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    Free Rent Tracking & Automated Reminders
                  </p>
                  <p className="text-xs text-gray-600">Reduce late payments by 60%</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    RTB & RPZ Compliance Built-In
                  </p>
                  <p className="text-xs text-gray-600">Never miss a regulation update</p>
                </div>
              </div>
              <div className="flex items-start">
                <svg
                  className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Instant Access to Dashboard</p>
                  <p className="text-xs text-gray-600">Start managing in under 5 minutes</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Listing completion notice */}
        {fromCreateListing && (
          <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-r-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 font-medium">
                  <strong>Your listing is almost ready!</strong> Complete your account to publish
                  your property.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Google Sign Up Button */}
        <div className="space-y-4 mb-6">
          <button
            onClick={handleGoogleSignUp}
            disabled={isLoading}
            className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <img className="h-5 w-5 mr-2" src={googleLogoSvg} alt="Google" />
            Continue with Google
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or</span>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <div className="flex justify-between">
            <div
              className={`w-1/2 h-1 ${
                step === 1 ? 'bg-blue-500' : 'bg-gray-200'
              } rounded-full transition-all duration-300`}
            ></div>
            <div
              className={`w-1/2 h-1 ${
                step === 2 ? 'bg-blue-500' : 'bg-gray-200'
              } rounded-full transition-all duration-300`}
            ></div>
          </div>
        </div>
        <form onSubmit={step === 1 ? handleNextStep : createUser}>
          {error && (
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
              role="alert"
            >
              <span className="block sm:inline">{error}</span>
            </div>
          )}
          {step === 1 && (
            <>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="email">
                  Email address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Email address"
                  className="shadow-sm appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={signUpData.email}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-4">
                <label
                  className="block text-gray-700 text-sm font-semibold mb-2"
                  htmlFor="password"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••"
                    className="shadow-sm appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={signUpData.password}
                    onChange={handleInputChange}
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    )}
                  </button>
                </div>
                <ul className="text-xs mt-2 space-y-1">
                  {passwordRequirements.map((requirement) => {
                    const isValid = checkPasswordRequirement(requirement, signUpData.password);
                    return (
                      <li
                        key={requirement.id}
                        className={`flex items-center space-x-2 ${
                          signUpData.password
                            ? isValid
                              ? 'text-green-600'
                              : 'text-red-500'
                            : 'text-gray-500'
                        }`}
                      >
                        {signUpData.password && (
                          <span>
                            {isValid ? (
                              <svg
                                className="w-4 h-4 text-green-500"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path d="M5 13l4 4L19 7"></path>
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4 text-red-500"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path d="M6 18L18 6M6 6l12 12"></path>
                              </svg>
                            )}
                          </span>
                        )}
                        <span>{requirement.label}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
              <div className="mb-6">
                <label
                  className="block text-gray-700 text-sm font-semibold mb-2"
                  htmlFor="confirmPassword"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="••••••"
                    className="shadow-sm appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={signUpData.confirmPassword}
                    onChange={handleInputChange}
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 flex items-center pr-3"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={
                      showConfirmPassword ? 'Hide confirm password' : 'Show confirm password'
                    }
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                    )}
                  </button>
                </div>
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="name">
                  Full Name
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Full Name"
                  className="shadow-sm appearance-none border rounded-md w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  value={signUpData.name}
                  onChange={handleInputChange}
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-gray-700 text-sm font-semibold mb-2" htmlFor="phone">
                  Phone Number
                </label>
                <div className="relative">
                  <PhoneInput
                    country={'ie'}
                    value={signUpData.phone}
                    onChange={(phone) => setSignUpData((prev) => ({ ...prev, phone }))}
                    inputProps={{
                      name: 'phone',
                      required: true,
                      className:
                        'w-full pl-[4.5rem] pr-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                    }}
                    containerClass="w-full"
                    buttonClass="absolute left-0 top-0 bottom-0 flex items-center justify-center px-3 border-r border-gray-300"
                    dropdownClass="absolute w-full z-10"
                  />
                </div>
              </div>
              <div className="mb-6">
                {fromLandlordToolkit ? (
                  <>
                    <label className="block text-gray-700 text-sm font-semibold mb-2">
                      Account Type
                    </label>
                    <div className="bg-blue-50 border-2 border-blue-500 rounded-lg p-4">
                      <div className="flex items-center">
                        <svg
                          className="h-5 w-5 text-blue-600 mr-3"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <div>
                          <p className="font-semibold text-gray-900">Landlord Account</p>
                          <p className="text-sm text-gray-600">Full access to Landlord Toolkit</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2">
                      Want a different account type?{' '}
                      <button
                        type="button"
                        onClick={() => setSignUpData((prev) => ({ ...prev, role: '' }))}
                        className="text-blue-600 hover:underline"
                      >
                        Click here
                      </button>
                    </p>
                  </>
                ) : (
                  <>
                    <fieldset>
                      <legend className="block text-gray-700 text-sm font-semibold mb-2">
                        What would you like to do?*
                      </legend>
                      <div className="flex flex-col space-y-2">
                        {/* Renter */}
                        <label
                          htmlFor="role-renter"
                          className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition
                            ${signUpData.role === 'renter' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                        >
                          <input
                            id="role-renter"
                            type="radio"
                            name="role"
                            value="renter"
                            checked={signUpData.role === 'renter'}
                            onChange={handleInputChange}
                            aria-describedby="role-renter-hint"
                            className="peer sr-only"
                            required
                          />
                          <div className="flex items-start">
                            <div
                              className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center
                                ${signUpData.role === 'renter' ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                              aria-hidden="true"
                            >
                              {signUpData.role === 'renter' && (
                                <svg
                                  className="h-4 w-4 text-white"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.418 0l-3.5-3.5a1 1 0 011.418-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="ml-3">
                              <span className="text-sm font-medium text-gray-900">
                                I want to rent a property
                              </span>
                              <p id="role-renter-hint" className="text-xs text-gray-500 mt-1">
                                Browse active listings, book viewings, and apply easily.
                              </p>
                            </div>
                          </div>
                        </label>

                        {/* Landlord */}
                        <label
                          htmlFor="role-landlord"
                          className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition
                            ${signUpData.role === 'landlord' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                        >
                          <input
                            id="role-landlord"
                            type="radio"
                            name="role"
                            value="landlord"
                            checked={signUpData.role === 'landlord'}
                            onChange={handleInputChange}
                            aria-describedby="role-landlord-hint"
                            className="peer sr-only"
                          />
                          <div className="flex items-start">
                            <div
                              className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center
                                ${signUpData.role === 'landlord' ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                              aria-hidden="true"
                            >
                              {signUpData.role === 'landlord' && (
                                <svg
                                  className="h-4 w-4 text-white"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.418 0l-3.5-3.5a1 1 0 011.418-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="ml-3">
                              <span className="text-sm font-medium text-gray-900">
                                I want to list a property
                              </span>
                              <p id="role-landlord-hint" className="text-xs text-gray-500 mt-1">
                                Create listings, manage inquiries, and track performance.
                              </p>
                            </div>
                          </div>
                        </label>

                        {/* Agent */}
                        <label
                          htmlFor="role-agent"
                          className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition
                            ${signUpData.role === 'agent' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                        >
                          <input
                            id="role-agent"
                            type="radio"
                            name="role"
                            value="agent"
                            checked={signUpData.role === 'agent'}
                            onChange={handleInputChange}
                            aria-describedby="role-agent-hint"
                            className="peer sr-only"
                          />
                          <div className="flex items-start">
                            <div
                              className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center
                                ${signUpData.role === 'agent' ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                              aria-hidden="true"
                            >
                              {signUpData.role === 'agent' && (
                                <svg
                                  className="h-4 w-4 text-white"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.418 0l-3.5-3.5a1 1 0 011.418-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="ml-3">
                              <span className="text-sm font-medium text-gray-900">
                                I manage properties for clients
                              </span>
                              <p id="role-agent-hint" className="text-xs text-gray-500 mt-1">
                                Use the toolkit to manage multiple properties efficiently.
                              </p>
                            </div>
                          </div>
                        </label>

                        {/* FSBO */}
                        <label
                          htmlFor="role-fsbo"
                          className={`relative flex items-start p-4 border rounded-lg cursor-pointer transition
                            ${signUpData.role === 'fsbo' ? 'border-blue-500 ring-2 ring-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`}
                        >
                          <input
                            id="role-fsbo"
                            type="radio"
                            name="role"
                            value="fsbo"
                            checked={signUpData.role === 'fsbo'}
                            onChange={handleInputChange}
                            aria-describedby="role-fsbo-hint"
                            className="peer sr-only"
                          />
                          <div className="flex items-start">
                            <div
                              className={`mt-1 h-5 w-5 rounded-md border flex items-center justify-center
                                ${signUpData.role === 'fsbo' ? 'bg-blue-600 border-blue-600' : 'border-gray-300 bg-white'}`}
                              aria-hidden="true"
                            >
                              {signUpData.role === 'fsbo' && (
                                <svg
                                  className="h-4 w-4 text-white"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M16.704 5.29a1 1 0 010 1.42l-7.5 7.5a1 1 0 01-1.418 0l-3.5-3.5a1 1 0 011.418-1.42l2.79 2.79 6.79-6.79a1 1 0 011.42 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </div>
                            <div className="ml-3">
                              <span className="text-sm font-medium text-gray-900">
                                I want to sell my home
                              </span>
                              <p id="role-fsbo-hint" className="text-xs text-gray-500 mt-1">
                                Sell directly (For Sale By Owner) with guided steps.
                              </p>
                            </div>
                          </div>
                        </label>
                      </div>
                    </fieldset>
                  </>
                )}
              </div>
              <div className="mb-6 space-y-3">
                <label className="inline-flex items-start">
                  <input
                    type="checkbox"
                    checked={signUpData.marketingConsent}
                    onChange={(e) =>
                      setSignUpData((prev) => ({
                        ...prev,
                        marketingConsent: e.target.checked,
                      }))
                    }
                    className="form-checkbox text-blue-500 mt-1"
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    Yes, I'd like to receive exclusive property updates, personalized
                    recommendations, and special offers from RentSmart.ie. I can unsubscribe
                    anytime.
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="checkbox"
                    checked={agreeToTerms}
                    onChange={(e) => setAgreeToTerms(e.target.checked)}
                    className="form-checkbox text-blue-500"
                    required
                  />
                  <span className="ml-2 text-sm text-gray-700">
                    I agree to the{' '}
                    <Link
                      to="/terms-of-service"
                      className="text-blue-500 hover:underline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms of Service
                    </Link>
                  </span>
                </label>
              </div>
            </>
          )}
          <div className="flex items-center justify-between">
            {step === 2 && (
              <button
                type="button"
                onClick={handlePrevStep}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline"
              >
                Back
              </button>
            )}
            <button
              className={`${
                step === 1 ? 'w-full' : 'w-1/2 ml-2'
              } bg-blue-500 hover:bg-blue-600 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:shadow-outline flex items-center justify-center`}
              type={step === 1 ? 'button' : 'submit'}
              onClick={step === 1 ? handleNextStep : undefined}
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 024 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Account...
                </>
              ) : step === 1 ? (
                'Next'
              ) : fromLandlordToolkit ? (
                'Get Free Access →'
              ) : (
                'Create Account'
              )}
            </button>
          </div>
        </form>

        {/* Landlord-specific trust indicators */}
        {fromLandlordToolkit && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center gap-6 text-xs text-gray-600">
              <div className="flex items-center gap-1">
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">100% Free</span>
              </div>
              <div className="flex items-center gap-1">
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">No Credit Card</span>
              </div>
              <div className="flex items-center gap-1">
                <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                <span className="font-medium">1,000+ Landlords</span>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-center">
          <p className="text-gray-600">
            Already have an account?{' '}
            <a
              href={returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login'}
              className="text-blue-500 hover:text-blue-700 font-semibold"
            >
              Go to login
            </a>
          </p>
        </div>
      </div>
      </div>
    </>
  );
};
